// ProposalPrimitives — shared visual atoms for the Proposal Coach.
//
// Kept in a separate file so we can iterate on the Coach's look
// without touching the 23k-line Nuggets.jsx.  Everything here is
// stateless, no data fetching, no side-effects — pure display.
//
// Exports:
//   * ModelBadge      — chip showing which AI model produced a draft
//                       (fable-5 gets its own iconography so users
//                       see when the upgraded model is in play).
//   * StatusChip      — uniform section-status pill.
//   * TokenCostChip   — cost badge with model breakdown on hover.
//   * FableIntroBanner— dismissible one-time "Now powered by Fable 5"
//                       hero for the Coach.

import React from 'react';
import { Sparkles, Brain, Zap, CheckCircle2, Clock, Eye, XCircle, DollarSign, Wand2 } from 'lucide-react';

// ── Model metadata ──────────────────────────────────────────────
// Keys match ANTHROPIC model IDs used across api/*.js. If we add
// a model, add it here so the chip picks up the right icon + tone.
const MODEL_META = {
  'claude-fable-5':             { label: 'Fable 5',   short: 'F5',  color: '#F59E0B', icon: Wand2,     kind: 'creative',   hint: 'Fable 5 — Anthropic\'s creative-writing model. Best for prose voice and tone.' },
  'claude-sonnet-4-6':          { label: 'Sonnet 4.6', short: 'S46', color: '#60A5FA', icon: Brain,     kind: 'reasoning',  hint: 'Sonnet 4.6 — deep reasoning. Best for analytical work.' },
  'claude-opus-4-8':            { label: 'Opus 4.8',   short: 'O48', color: '#A78BFA', icon: Sparkles,  kind: 'reasoning+', hint: 'Opus 4.8 — flagship reasoning.' },
  'claude-opus-4-7':            { label: 'Opus 4.7',   short: 'O47', color: '#A78BFA', icon: Sparkles,  kind: 'reasoning+', hint: 'Opus 4.7 — reasoning.' },
  'claude-opus-4-6':            { label: 'Opus 4.6',   short: 'O46', color: '#A78BFA', icon: Sparkles,  kind: 'reasoning+', hint: 'Opus 4.6 — reasoning.' },
  'claude-haiku-4-5-20251001':  { label: 'Haiku 4.5',  short: 'H45', color: '#22C55E', icon: Zap,       kind: 'fast',       hint: 'Haiku 4.5 — fast + cheap. Best for extraction and tagging.' },
};

const UNKNOWN_MODEL = { label: 'AI',        short: 'AI', color: '#94A3B8', icon: Sparkles, kind: 'unknown', hint: 'AI model' };

export function metaForModel(model) {
  if (!model) return UNKNOWN_MODEL;
  return MODEL_META[model] || UNKNOWN_MODEL;
}

// ── ModelBadge ──────────────────────────────────────────────────
// Small chip. `size='sm' | 'md'`, `variant='solid' | 'outline'`.
export function ModelBadge({ model, size = 'sm', variant = 'outline', showLabel = true, title }) {
  const m = metaForModel(model);
  const Icon = m.icon;
  const isSolid = variant === 'solid';
  const pad     = size === 'md' ? '4px 8px' : '2px 6px';
  const fs      = size === 'md' ? 11 : 10;
  const gap     = size === 'md' ? 6 : 4;
  const iconSz  = size === 'md' ? 12 : 10;
  return (
    <span
      title={title || m.hint}
      style={{
        display: 'inline-flex', alignItems: 'center', gap,
        padding: pad, borderRadius: 4,
        background: isSolid ? m.color : `${m.color}18`,
        color:      isSolid ? '#fff'  : m.color,
        border: `1px solid ${isSolid ? m.color : m.color + '55'}`,
        fontFamily: "'Montserrat',sans-serif",
        fontSize: fs, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase',
        lineHeight: 1,
      }}
    >
      <Icon size={iconSz} />
      {showLabel && (size === 'md' ? m.label : m.short)}
    </span>
  );
}

// ── StatusChip ─────────────────────────────────────────────────
const STATUS_META = {
  draft:      { label: 'Draft',      color: '#94A3B8', icon: Clock         },
  in_review:  { label: 'In Review',  color: '#F59E0B', icon: Eye           },
  approved:   { label: 'Approved',   color: '#22C55E', icon: CheckCircle2  },
  rejected:   { label: 'Rejected',   color: '#EF4444', icon: XCircle       },
  final:      { label: 'Final',      color: '#22C55E', icon: CheckCircle2  },
  ready:      { label: 'Ready',      color: '#60A5FA', icon: CheckCircle2  },
};

export function StatusChip({ status, size = 'sm' }) {
  const m = STATUS_META[status] || { label: status || 'unknown', color: '#94A3B8', icon: Clock };
  const Icon = m.icon;
  const pad    = size === 'md' ? '4px 10px' : '3px 8px';
  const fs     = size === 'md' ? 11 : 10;
  const iconSz = size === 'md' ? 12 : 10;
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: pad, borderRadius: 999,
        background: `${m.color}15`,
        color: m.color,
        border: `1px solid ${m.color}55`,
        fontFamily: "'Montserrat',sans-serif",
        fontSize: fs, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
        lineHeight: 1,
      }}
    >
      <Icon size={iconSz} />
      {m.label}
    </span>
  );
}

// ── TokenCostChip ──────────────────────────────────────────────
// Compact "$0.34" pill with a details tooltip. Optional breakdown
// array of {model, inputTokens, outputTokens, cost}.
export function TokenCostChip({ costUsd, breakdown, size = 'sm' }) {
  const c = Number(costUsd) || 0;
  const label = c === 0 ? '—' : c < 0.01 ? '<$0.01' : `$${c.toFixed(2)}`;
  const tone = c === 0 ? '#94A3B8' : c < 0.10 ? '#22C55E' : c < 0.50 ? '#F59E0B' : '#EF4444';

  const [tipOpen, setTipOpen] = React.useState(false);
  const pad    = size === 'md' ? '4px 10px' : '3px 8px';
  const fs     = size === 'md' ? 11 : 10;
  const iconSz = size === 'md' ? 11 : 9;

  return (
    <span
      onMouseEnter={() => setTipOpen(true)}
      onMouseLeave={() => setTipOpen(false)}
      style={{
        position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: pad, borderRadius: 4,
        background: `${tone}12`,
        color: tone,
        border: `1px solid ${tone}44`,
        fontFamily: "'Montserrat',sans-serif",
        fontSize: fs, fontWeight: 800, letterSpacing: '0.04em',
        lineHeight: 1, cursor: breakdown?.length ? 'help' : 'default',
      }}
    >
      <DollarSign size={iconSz} />
      {label}
      {breakdown?.length > 0 && tipOpen && (
        <span
          role="tooltip"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0,
            padding: '8px 10px', borderRadius: 6,
            background: '#0D1B2A', border: '1px solid rgba(255,255,255,0.12)',
            color: '#F0F4F8', fontSize: 10, whiteSpace: 'nowrap',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)', zIndex: 20,
            display: 'flex', flexDirection: 'column', gap: 4,
          }}
        >
          {breakdown.map((b, i) => (
            <span key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
              <ModelBadge model={b.model} size="sm" variant="outline" showLabel />
              <span style={{ color: 'rgba(255,255,255,0.85)' }}>
                {(b.inputTokens || 0).toLocaleString()}→{(b.outputTokens || 0).toLocaleString()}t
              </span>
              <span style={{ color: tone, fontWeight: 800 }}>${(b.cost || 0).toFixed(3)}</span>
            </span>
          ))}
        </span>
      )}
    </span>
  );
}

// ── FableIntroBanner ────────────────────────────────────────────
// One-time hero at the top of the Proposal Coach announcing the
// Fable 5 upgrade. Dismissible; dismissal persists to localStorage.
const FABLE_BANNER_KEY = 'proposal:fable5-banner:v1';

export function FableIntroBanner() {
  const [dismissed, setDismissed] = React.useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(FABLE_BANNER_KEY) === '1';
  });
  if (dismissed) return null;
  const dismiss = () => {
    try { window.localStorage.setItem(FABLE_BANNER_KEY, '1'); } catch {}
    setDismissed(true);
  };
  return (
    <div
      style={{
        position: 'relative',
        margin: '0 0 14px 0', padding: '14px 46px 14px 18px',
        borderRadius: 8,
        background: 'linear-gradient(135deg, rgba(245,158,11,0.18) 0%, rgba(232,119,34,0.10) 100%)',
        border: '1px solid rgba(245,158,11,0.35)',
        color: '#F5F0E6',
        fontFamily: "'Montserrat',sans-serif",
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <Wand2 size={16} color="#F59E0B" />
        <span style={{ fontSize: 10, letterSpacing: '0.14em', color: '#F59E0B', fontWeight: 800, textTransform: 'uppercase' }}>
          Proposal Coach · Powered by Fable 5
        </span>
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: 'rgba(255,255,255,0.9)' }}>
        Section drafts, the full-proposal generator, and the coaching assistant now route through
        <b> Fable 5</b> — Anthropic's dedicated creative-writing model. Expect stronger tone,
        cleaner prose, and better handling of the audience-tone slider. Analytical tools (gap radar,
        critique, win-probability) stay on Sonnet 4.6 for their reasoning depth.
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss Fable 5 banner"
        style={{
          position: 'absolute', top: 10, right: 10,
          width: 24, height: 24, borderRadius: 6,
          background: 'transparent', border: '1px solid rgba(255,255,255,0.14)',
          color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 14, lineHeight: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        ×
      </button>
    </div>
  );
}
